import { yupResolver } from '@hookform/resolvers/yup';
import { Calendar } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CommonModal from 'src/components/CommonModal';
import DateSelect from 'src/components/DateSelect/DateSelect';
import { addressDetailsData, setActiveQuotes, setAddressDetails, setPredictiveIndex } from 'src/redux/Quoting.slice';
import { updatePickupDate } from 'src/services/QuoteService';
import { createDateWithoutTimezoneConversion, GetAppearance, getDateWithSuffixFormat, getFormattedDateIgnoringTime } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';
import * as yup from 'yup';

import { SENSITIVITY } from '../CreateQuote/Steps/Steps.const';
import { IDatePickupForm, IDatePickupModel } from '../QuoteDetail/quote.interface';

// Validate that pickupEndDate is on or after pickupDate (day-level comparison).
// Defined outside the schema factory to avoid recreating on every render.
const validatePickupEndAfterStart = (value: any, context: yup.TestContext) => {
    const { pickupDate } = context.parent;

    if (!value || !pickupDate) return true;

    return moment(value).startOf('day').isSameOrAfter(moment(pickupDate).startOf('day'));
};

const DatePickupModel: FC<IDatePickupModel> = ({ handleClose, onUpdate, setChangedByCustomer }) => {
    const { t } = useTranslation();
    const appearance = GetAppearance();
    const dispatch = useDispatch();
    const addressDetails = useSelector(addressDetailsData);
    const [isLoading, setIsLoading] = useState(false);

    // Determine whether the shipper has a flexible pickup window — controls end-date field visibility and validation.
    const isFlexible = addressDetails?.pickupSensitivity === SENSITIVITY.FLEXIBLE;

    const validationSchema = yup.object().shape({
        pickupDate: yup
            .date()
            .required(t('ValidationMessages.Pickup_Date_is_required'))
            .test('not-in-past', t('ValidationMessages.Pickup_date_cannot_be_in_the_past'), function (value) {
                if (!value) return true;
                const currentDateString = moment().tz(appearance.timezone).format('YYYY-MM-DD');
                const pickupDateString = moment(value).startOf('day').format('YYYY-MM-DD');

                return !moment(pickupDateString).isBefore(moment(currentDateString));
            }),
        pickupEndDate: yup.date().when([], {
            is: () => isFlexible,
            then: (s) => s.required(t('ValidationMessages.Pickup_End_Date_is_required')).test('after-pickup', t('ValidationMessages.Pickup_end_date_must_be_after_start'), validatePickupEndAfterStart),
            otherwise: (s) => s.optional(),
        }),
    });

    const defaultValues = useMemo(
        () => ({
            pickupDate: addressDetails?.pickupDate,
            pickupEndDate: addressDetails?.pickupEndDate ?? undefined,
        }),
        [addressDetails?.pickupDate, addressDetails?.pickupEndDate]
    );

    const {
        control,
        handleSubmit,
        setError,
        setValue,
        watch,
        formState: { isDirty },
    } = useForm<IDatePickupForm>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues,
    });

    const currentPickupDate = watch('pickupDate');
    const currentPickupEndDate = watch('pickupEndDate');

    // When the start date moves past the end date, clear the end date so the user must re-select.
    useEffect(() => {
        if (isFlexible && currentPickupDate && currentPickupEndDate && moment(currentPickupDate).startOf('day').isAfter(moment(currentPickupEndDate).startOf('day'))) {
            setValue('pickupEndDate', null, { shouldDirty: true });
        }
    }, [currentPickupDate]);

    const onSubmit = (data: IDatePickupForm) => {
        setIsLoading(true);

        const formattedPickupDate = moment(data.pickupDate).format('YYYY-MM-DD 00:00:00');

        // Determine if the existing delivery date needs to be cleared because pickup now falls after it.
        const existingDeliveryDate = addressDetails?.deliveryDate;
        const isPickupAfterDelivery = existingDeliveryDate && moment(data.pickupDate).startOf('day').isAfter(moment(existingDeliveryDate).startOf('day'));
        const isPickupEndAfterDelivery = isFlexible && data.pickupEndDate && existingDeliveryDate && moment(data.pickupEndDate).startOf('day').isAfter(moment(existingDeliveryDate).startOf('day'));
        const shouldClearDelivery = Boolean(isPickupAfterDelivery || isPickupEndAfterDelivery);

        // Only send pickupEndDate when the shipper window is flexible.
        const formattedPickupEndDate = isFlexible && data.pickupEndDate ? moment(data.pickupEndDate).format('YYYY-MM-DD 00:00:00') : null;

        updatePickupDate(addressDetails.id, formattedPickupDate, formattedPickupEndDate)
            .then((result: any) => {
                const newPickupDate = result?.data?.address?.pickupDate;
                const newDeliveryDate = shouldClearDelivery ? null : result?.data?.address?.deliveryDate ?? null;

                // Update Redux state with new dates. Clear delivery if pickup moved past it.
                dispatch(
                    setAddressDetails({
                        pickupDate: newPickupDate,
                        pickupEndDate: result?.data?.address?.pickupEndDate ?? null,
                        deliveryDate: newDeliveryDate,
                    })
                );

                if (result?.data?.predictiveIndexData) {
                    dispatch(setPredictiveIndex(result?.data?.predictiveIndexData));
                }

                dispatch(setActiveQuotes([]));

                if (setChangedByCustomer) {
                    setChangedByCustomer(true);
                }

                // Notify parent to update its local state directly, avoiding an extra API call.
                onUpdate?.(newPickupDate, newDeliveryDate, result?.data?.address?.pickupEndDate ?? null);

                WalToast.success(t('ValidationMessages.Pickup_date_updated'));
                handleClose();
            })
            .catch(async () => {
                setError('pickupDate', {
                    message: t('ValidationMessages.Pickup_date_failed'),
                });
                WalToast.error(t('ValidationMessages.Pickup_date_failed'));
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDateChange = (onChange: any) => (event: any) => {
        const { value } = event;
        onChange(value);
    };

    return (
        <CommonModal
            title={t('QuotingHub.QuotingDashboard.Update_Pickup_Date')}
            modalTitleDesc={t('QuotingHub.QuotingDashboard.Pickup_Date_Update_desc')}
            handleClose={handleClose}
            size={'max-w-[400px]'}
            modalHeaderClass="border-b-0 !px-5 !pt-6 !pb-5 !bg-transparent"
            modalFooterClass="border-t-0 !bg-transparent"
            closeBtnClass="-mt-2 -mr-2"
            primaryBtnOnClick={handleSubmit(onSubmit)}
            primaryBtnText={t('Common.Save')}
            secondaryBtnOnClick={handleClose}
            secondaryBtnText={t('Common.Cancel')}
            primaryBtnLoading={isLoading}
            primaryBtnDisabled={isLoading || !isDirty || (isFlexible && !currentPickupEndDate)}
        >
            <div className="px-5 pb-3 flex flex-col gap-3">
                <Controller
                    name="pickupDate"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <DateSelect
                            label={t('Common.Pickup_Date')}
                            inputName="pickupDate"
                            className={`form_control ${error ? 'border border-red-500 mb-1 border-solid' : ''}`}
                            placeholder="Select pickup date"
                            dateFormat="dd/MM/yyyy"
                            minDate={createDateWithoutTimezoneConversion(moment.tz(appearance.timezone))}
                            selected={value}
                            value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                            onChangeFunc={handleDateChange(onChange)}
                            icon={<Calendar className="h-5 w-5" />}
                            parentClassName="w-full"
                            required
                        />
                    )}
                />

                {isFlexible && (
                    <Controller
                        name="pickupEndDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DateSelect
                                label={t('Common.Pickup_End_Date')}
                                inputName="pickupEndDate"
                                className={`form_control ${error ? 'border border-red-500 mb-1 border-solid' : ''}`}
                                placeholder="Select pickup end date"
                                dateFormat="dd/MM/yyyy"
                                minDate={currentPickupDate ? createDateWithoutTimezoneConversion(moment(currentPickupDate)) : createDateWithoutTimezoneConversion(moment.tz(appearance.timezone))}
                                selected={value}
                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                onChangeFunc={handleDateChange(onChange)}
                                icon={<Calendar className="h-5 w-5" />}
                                parentClassName="w-full"
                                required
                            />
                        )}
                    />
                )}
            </div>
        </CommonModal>
    );
};

export default DatePickupModel;
